import React, { useState, useEffect } from 'react';
import { DollarSign, Plus, Edit, Trash, Briefcase, Calculator, CheckCircle, XCircle } from 'lucide-react';
import api from '../api/client';
import { useNotification } from './RealTimeNotifications';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showNotification } = useNotification();

  // Form state
  const [formData, setFormData] = useState({
    lead_id: '',
    property_name: '',
    price: '',
    currency: 'AED',
    agency_commission_percent: 2.0,
    status: 'mou_signed',
    splits: [{ agent_name: '', split_percent: 50.0 }]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const tenantId = localStorage.getItem('tenantId') || '1';
      // Fetch transactions
      const txRes = await api.get(`/api/tenants/${tenantId}/transactions`);
      setTransactions(txRes || []);
      
      // Fetch leads for the dropdown
      const leadsRes = await api.get(`/api/tenants/${tenantId}/unified-leads`);
      setLeads(leadsRes.items || leadsRes || []);
    } catch (err) {
      console.error('Failed to fetch transactions data:', err);
      showNotification('error', 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSplit = () => {
    setFormData({
      ...formData,
      splits: [...formData.splits, { agent_name: '', split_percent: 0 }]
    });
  };

  const handleSplitChange = (index, field, value) => {
    const newSplits = [...formData.splits];
    newSplits[index][field] = value;
    setFormData({ ...formData, splits: newSplits });
  };

  const handleRemoveSplit = (index) => {
    const newSplits = formData.splits.filter((_, i) => i !== index);
    setFormData({ ...formData, splits: newSplits });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const tenantId = localStorage.getItem('tenantId') || '1';
      
      // Convert strings to numbers
      const payload = {
        ...formData,
        lead_id: parseInt(formData.lead_id),
        price: parseFloat(formData.price),
        agency_commission_percent: parseFloat(formData.agency_commission_percent),
        splits: formData.splits.map(s => ({
          agent_name: s.agent_name,
          split_percent: parseFloat(s.split_percent)
        }))
      };

      await api.post(`/api/tenants/${tenantId}/transactions`, payload);
      showNotification('success', 'Transaction Created Successfully');
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('error', err.response?.data?.detail || 'Failed to create transaction');
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const tenantId = localStorage.getItem('tenantId') || '1';
      await api.put(`/api/tenants/${tenantId}/transactions/${id}/status?status=${newStatus}`);
      showNotification('success', 'Status updated');
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('error', 'Failed to update status');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-gold-400" />
            Transactions & Deals
          </h1>
          <p className="text-gray-400 text-sm mt-1">Manage signed MOUs, NOCs, transfers and commission splits.</p>
        </div>
        <button 
          onClick={() => {
            setFormData({
              lead_id: '', property_name: '', price: '', currency: 'AED',
              agency_commission_percent: 2.0, status: 'mou_signed',
              splits: [{ agent_name: localStorage.getItem('userName') || '', split_percent: 50.0 }]
            });
            setIsModalOpen(true);
          }} 
          className="btn-gold flex items-center gap-2"
        >
          <Plus size={18} />
          New Transaction
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><div className="w-8 h-8 border-4 border-gold-400 border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-navy-800/50 text-gray-400 text-xs uppercase">
                <tr>
                  <th className="px-6 py-4">Property & Lead</th>
                  <th className="px-6 py-4">Value</th>
                  <th className="px-6 py-4">Agency Comm.</th>
                  <th className="px-6 py-4">Agent Splits</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-20" />
                      <p>No transactions recorded yet.</p>
                    </td>
                  </tr>
                ) : transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-white">{tx.property_name}</div>
                      <div className="text-xs text-gray-400">Lead ID: {tx.lead_id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-green-400">
                        {tx.currency} {tx.price.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white">{tx.agency_commission_percent}%</div>
                      <div className="text-xs text-gold-400">{tx.currency} {tx.agency_commission_amount.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {tx.splits.map(s => (
                          <div key={s.id} className="flex justify-between text-xs bg-navy-800/50 rounded px-2 py-1">
                            <span className="text-gray-300">{s.agent_name} ({s.split_percent}%)</span>
                            <span className="text-white">{tx.currency} {s.split_amount.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={tx.status}
                        onChange={(e) => updateStatus(tx.id, e.target.value)}
                        className={`text-xs font-bold rounded-full px-3 py-1 bg-navy-900 border appearance-none cursor-pointer focus:outline-none focus:ring-1 ${
                          tx.status === 'transferred' ? 'text-green-400 border-green-500/30' :
                          tx.status === 'cancelled' ? 'text-red-400 border-red-500/30' :
                          'text-gold-400 border-gold-500/30'
                        }`}
                      >
                        <option value="mou_signed">MOU SIGNED</option>
                        <option value="noc_issued">NOC ISSUED</option>
                        <option value="transferred">TRANSFERRED</option>
                        <option value="cancelled">CANCELLED</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {/* Placeholder for future edit/delete */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Transaction Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-navy-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Calculator className="text-gold-400" /> 
                Record New Transaction
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white"><XCircle size={24}/></button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <form id="txForm" onSubmit={handleSubmit} className="space-y-6">
                
                {/* Deal Info */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gold-400 uppercase tracking-wider">1. Deal Details</h4>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Select Lead (Buyer/Tenant)</label>
                    <select 
                      required
                      value={formData.lead_id} 
                      onChange={e => setFormData({...formData, lead_id: e.target.value})}
                      className="input-field"
                    >
                      <option value="">-- Select a Lead --</option>
                      {leads.map(l => <option key={l.id} value={l.id}>{l.name} {l.phone ? `(${l.phone})` : ''}</option>)}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Property Name / Unit #</label>
                      <input 
                        required
                        type="text" 
                        value={formData.property_name} 
                        onChange={e => setFormData({...formData, property_name: e.target.value})}
                        className="input-field" 
                        placeholder="e.g. Marina Gate, Unit 402"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Status</label>
                      <select 
                        value={formData.status} 
                        onChange={e => setFormData({...formData, status: e.target.value})}
                        className="input-field"
                      >
                        <option value="mou_signed">MOU Signed</option>
                        <option value="noc_issued">NOC Issued</option>
                        <option value="transferred">Transferred / Completed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Financials */}
                <div className="space-y-4 pt-4 border-t border-white/10">
                  <h4 className="text-sm font-semibold text-gold-400 uppercase tracking-wider">2. Financials</h4>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm text-gray-400 mb-1">Deal Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">AED</span>
                        <input 
                          required
                          type="number" 
                          min="0" step="0.01"
                          value={formData.price} 
                          onChange={e => setFormData({...formData, price: e.target.value})}
                          className="input-field pl-12" 
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Agency Comm. %</label>
                      <div className="relative">
                        <input 
                          required
                          type="number" 
                          min="0" max="100" step="0.1"
                          value={formData.agency_commission_percent} 
                          onChange={e => setFormData({...formData, agency_commission_percent: e.target.value})}
                          className="input-field pr-8" 
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                      </div>
                    </div>
                  </div>
                  
                  {formData.price && formData.agency_commission_percent && (
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                      <span className="text-sm text-gray-400">Total Agency Commission: </span>
                      <span className="text-lg font-bold text-green-400">
                        AED {(parseFloat(formData.price) * (parseFloat(formData.agency_commission_percent) / 100)).toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Agent Splits */}
                <div className="space-y-4 pt-4 border-t border-white/10">
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-semibold text-gold-400 uppercase tracking-wider">3. Agent Commission Splits</h4>
                    <button type="button" onClick={handleAddSplit} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                      <Plus size={14}/> Add Agent
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {formData.splits.map((split, index) => (
                      <div key={index} className="flex items-center gap-3 bg-navy-800/50 p-3 rounded-lg border border-white/5">
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">Agent Name</label>
                          <input 
                            required
                            type="text" 
                            value={split.agent_name}
                            onChange={(e) => handleSplitChange(index, 'agent_name', e.target.value)}
                            className="w-full bg-navy-900 border border-white/10 rounded px-3 py-1.5 text-sm text-white" 
                            placeholder="e.g. John Doe"
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-xs text-gray-500 mb-1">Split % (of Agency)</label>
                          <div className="relative">
                            <input 
                              required
                              type="number" 
                              min="0" max="100" step="0.1"
                              value={split.split_percent}
                              onChange={(e) => handleSplitChange(index, 'split_percent', e.target.value)}
                              className="w-full bg-navy-900 border border-white/10 rounded px-3 py-1.5 text-sm text-white" 
                            />
                          </div>
                        </div>
                        {formData.splits.length > 1 && (
                          <button type="button" onClick={() => handleRemoveSplit(index)} className="text-red-400 hover:text-red-300 mt-5">
                            <Trash size={18}/>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Validate Splits sum */}
                  {formData.splits.reduce((sum, s) => sum + (parseFloat(s.split_percent) || 0), 0) > 100 && (
                     <p className="text-xs text-red-400">Warning: Agent splits exceed 100% of the agency commission.</p>
                  )}
                </div>

              </form>
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3 bg-navy-800/30">
              <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-400 hover:text-white font-medium">
                Cancel
              </button>
              <button type="submit" form="txForm" className="btn-gold flex items-center gap-2">
                <CheckCircle size={18} />
                Save Transaction
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Transactions;
